import { isExpression, getIdentifierPaths } from '@code2io/common-flow-utilities';
import { get } from 'lodash';
import React, { useContext, useState, useMemo, useEffect } from 'react';
import { componentCreator } from '../api/childrenManager.js';
import { propChecker, getAttrValue } from '../api/propChecker.js';
import { useAppDispatch, useAppSelector } from '../hooks/hooks.js';
import { store } from '../store/index.js';
import { selectMode } from '../store/reducers/enginePropertiesSlice.js';
import { selectVariablesWithDescriptors, setVariableProp } from '../store/reducers/variablesSlice.js';
import { PageIdContext } from './pageIdContext.js';
import RenderPredicate from './RenderPredicate.js';
import { isEqual } from '../../util/isEqual.js';

const parseProps = (node, paths) => {
    if (node === null)
        return;
    else if (typeof node === "string") {
        paths.push(getIdentifierPaths(node));
    }
    else if (Array.isArray(node)) {
        node.forEach((ele) => parseProps(ele, paths));
    }
    else if (typeof node === "object") {
        Object.values(node).forEach((val) => parseProps(val, paths));
    }
};
const getVariablePaths = (props) => {
    const paths = [];
    parseProps(props, paths);
    return paths;
};
const GenericWrapper = ({ comp, children, item }) => {
    const pageId = useContext(PageIdContext);
    const dispatch = useAppDispatch();
    const { props, propMask } = comp;
    const mode = useAppSelector(selectMode);
    const [initialized, setInitialized] = useState(false);
    const variableDescriptors = useMemo(() => {
        const res = [];
        const allPropsPaths = getVariablePaths(props);
        allPropsPaths.forEach((paths) => {
            paths.forEach((path) => {
                if (path.length < 2) {
                    return;
                }
                res.push({
                    scope: path[0],
                    name: path[1]
                });
            });
        });
        return {
            descriptors: res,
            pageId
        };
    }, [props, pageId]);
    const variableDescriptorsMask = useMemo(() => {
        const res = [];
        const allPropMaskPaths = getVariablePaths(propMask !== null && propMask !== void 0 ? propMask : {});
        allPropMaskPaths.forEach((paths) => {
            paths.forEach((path) => {
                if (path.length < 2) {
                    return;
                }
                res.push({
                    scope: path[0],
                    name: path[1]
                });
            });
        });
        return {
            descriptors: res,
            pageId
        };
    }, [propMask, pageId]);
    const variables = useAppSelector((state) => selectVariablesWithDescriptors(state, variableDescriptors));
    const maskVars = useAppSelector((state) => selectVariablesWithDescriptors(state, variableDescriptorsMask));
    const propMaskToPropBinding = (obj, objMask) => {
        for (const prop in obj) {
            if (typeof objMask !== "undefined" &&
                objMask !== null &&
                typeof objMask[prop] !== "undefined") {
                if (typeof obj[prop] !== "string") {
                    if (typeof obj[prop] === "object" &&
                        !Array.isArray(obj[prop]) &&
                        obj[prop] !== null) {
                        propMaskToPropBinding(obj[prop], objMask[prop]);
                    }
                    continue;
                }
                if (!isExpression(obj[prop]))
                    continue;
                const paths = obj[prop].slice(2, -2).split(".");
                paths.splice(1, 0, pageId);
                paths.splice(3, 0, "value");
                if (typeof prop === "undefined" || (!objMask[prop] && mode === "live"))
                    continue;
                let value = getAttrValue(objMask[prop], Object.assign(Object.assign({}, variables), maskVars));
                const newPath = obj[prop].slice(2, -2).split(".");
                const currentVal = get(variables, `${newPath.join(".").split(" ")[0]}`);
                if (!isEqual(currentVal, value)) {
                    dispatch(setVariableProp({
                        scope: "page",
                        name: `${paths.join(".").split(" ")[0]}`,
                        value
                    }));
                }
            }
        }
    };
    useEffect(() => {
        propMaskToPropBinding(props, propMask);
        setInitialized(true);
    }, [maskVars]);
    const newChildren = children && children.length > 0
        ? children
        : propChecker(comp.children, Object.assign(Object.assign({}, variables), { item: item }), comp, store.getState().components);
    const node = componentCreator(comp, {
        templates: store.getState().components,
        newChildren,
        variables: Object.assign(Object.assign({}, variables), { item: item })
    });
    return React.createElement(RenderPredicate, { predicate: initialized }, node);
};

export { GenericWrapper as default };
//# sourceMappingURL=GenericWrapper.js.map
